import { Breakpoint } from '../components/core/types/globalTypes';
import { isNullOrUndefined } from './collection';
import config from './../styles/settings/configs/breakpoints-config.json';

/**
 * Match media types
 */
export enum MediaTypes {
  minWidth = 'min-width',
  maxWidth = 'max-width',
}

/**
 * Returns the list all of breakpoint keys
 * @returns {Breakpoint[]} The list of breakpoints
 */
export const getBreakpoints = (): Breakpoint[] => Object.keys(config.breakpoints) as Breakpoint[];

/**
 * Returns the breakpoint in pixels
 * @param {Breakpoint} breakpoint The breakpoint
 * @returns {number} The breakpoint value in pixels
 */
export const getBreakpointInPixels = (breakpoint: Breakpoint): number => parseInt(config.breakpoints[breakpoint], 10);

/**
 * Returns the breakpoint key for given pixels
 * @param {number} pixels The pixels of the breakpoint
 * @returns {Breakpoint} The breakpoint key
 */
export const getBreakpointFromPixels = (pixels: number): Breakpoint =>
  Object.keys(config.breakpoints)[
    Object.values(config.breakpoints)
      .map((bp) => parseInt(bp, 10))
      .findIndex((bpPixels) => bpPixels === pixels)
  ] as Breakpoint;

const mediaQueryLists: MediaQueryList[] = [];

/**
 * Adds matchMedia to window with the given properties.
 * @param {MediaTypes} mediaType The type of media
 * @param {Sizes} breakpoint The media size
 * @param {Function} listenerFunction Callback function for the listener
 */
export const addMatchMedia = (
  mediaType: MediaTypes,
  breakpoint: Breakpoint,
  listenerFunction: (this: MediaQueryList, ev: MediaQueryListEvent) => unknown
): void => {
  const foundMatchMedia = mediaQueryLists.find((m) => m.media === `(${mediaType}: ${config.breakpoints[breakpoint]})`);
  if (!isNullOrUndefined(foundMatchMedia)) {
    foundMatchMedia.addEventListener('change', listenerFunction);
  } else {
    const newMatchMedia = window.matchMedia(`(${mediaType}: ${config.breakpoints[breakpoint]})`);
    mediaQueryLists.push(newMatchMedia);
    newMatchMedia.addEventListener('change', listenerFunction);
  }
};

/**
 * Removes matchMedia from window with the given properties.
 * @param {MediaTypes} mediaType The type of media
 * @param {Sizes} breakpoint The media size
 * @param {Function} listenerFunction Callback function to be removed from the listener
 */
export const removeMatchMedia = (
  mediaType: MediaTypes,
  breakpoint: Breakpoint,
  listenerFunction: (this: MediaQueryList, ev: MediaQueryListEvent) => unknown
): void => {
  mediaQueryLists.find((m) => m.media === `(${mediaType}: ${config.breakpoints[breakpoint]})`)?.removeEventListener('change', listenerFunction);
};

/**
 * Matches the current window with the given media type and breakpoint
 * @param {MediaTypes} mediaType The media type
 * @param {Breakpoint} breakpoint The breakpoint
 * @returns {boolean} True if it matches, false otherwise
 */
export const mediaMatchingBreakpoint = (mediaType: MediaTypes, breakpoint: Breakpoint): boolean => {
  const foundMatchMedia = mediaQueryLists.find((m) => m.media === `(${mediaType}: ${config.breakpoints[breakpoint]})`);
  if (!isNullOrUndefined(foundMatchMedia)) {
    return foundMatchMedia.matches;
  }
  const newMatchMedia = window.matchMedia(`(${mediaType}: ${config.breakpoints[breakpoint]})`);
  mediaQueryLists.push(newMatchMedia);
  return newMatchMedia.matches;
};
